<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import closeIcon from '~/assets/images/icons/close.svg';
import { useInstallPrompt } from '~/composables/useInstallPrompt';
import IosInstallInstructionsModal from './IosInstallInstructionsModal.vue';

const { platform, canShowBanner, promptInstall, dismiss, setBannerMounted } =
  useInstallPrompt();

// Only the homepage mounts this, so the layout — which reserves the space the
// fixed banner covers — needs telling that it is present.
onMounted(() => setBannerMounted(true));
onUnmounted(() => setBannerMounted(false));

const modalVisible = ref(false);

async function onInstall() {
  // iOS exposes no programmatic install, so the only thing to offer is the
  // manual walkthrough. Everything else has a captured native prompt by
  // definition — `platform` only reports 'android' once one exists.
  if (platform.value === 'ios') {
    modalVisible.value = true;
    return;
  }

  await promptInstall();
}

function onDismiss() {
  modalVisible.value = false;
  dismiss();
}
</script>

<template>
  <div v-if="canShowBanner" class="install-banner" data-testid="install-banner">
    <button
      type="button"
      class="install-banner__close"
      aria-label="Dismiss install prompt"
      data-testid="install-banner-close"
      @click="onDismiss"
    >
      <img :src="closeIcon" alt="" width="24" height="24" />
    </button>

    <div class="install-banner__copy">
      <img
        src="/logo-mobile.svg"
        alt="VoteWise"
        width="129"
        height="24"
        class="install-banner__logo"
        data-testid="install-banner-logo"
      />
      <p class="install-banner__message">Know who you're voting for.</p>
    </div>

    <button
      type="button"
      class="install-banner__cta"
      data-testid="install-banner-cta"
      @click="onInstall"
    >
      Install
    </button>

    <IosInstallInstructionsModal
      v-model:visible="modalVisible"
      @cancel="onDismiss"
      @confirm="onDismiss"
    />
  </div>
</template>

<style lang="scss" scoped>
.install-banner {
  // Fixed rather than in-flow: the design anchors this to the bottom of the
  // screen. `app/layouts/default.vue` reserves matching space below the footer
  // so the whole page, footer included, can still scroll clear of it.
  position: fixed;
  inset-inline: 0;
  bottom: 0;
  z-index: 20;

  display: flex;
  align-items: center;
  gap: $spacing-md;
  // The Figma 24px stays the baseline; the inset is 0px on devices without a
  // home indicator, and lifts the row clear of the gesture bar on those with.
  padding: $spacing-lg $spacing-lg
    calc(#{$spacing-xl} + env(safe-area-inset-bottom));
  background-color: $surface-color-bg;
  @include box-shadow(0 -4px 4px rgba(33, 33, 35, 0.05));

  // Mobile-only, per the design; desktop has no install banner.
  @media (min-width: 1024px) {
    display: none;
  }

  &__close {
    display: flex;
    flex-shrink: 0;
    cursor: pointer;
    background: none;
    border: none;
    padding: 0;
  }

  &__copy {
    display: flex;
    flex-direction: column;
    gap: $spacing-sm;
    min-width: 0;
    flex: 1 1 auto;
  }

  &__logo {
    width: 129px;
    height: 24px;
  }

  &__message {
    @include font-subheadline;
    color: $text-color-subtext;
  }

  &__cta {
    @include font-subheadline-bold;
    flex-shrink: 0;
    height: 40px;
    padding-inline: $spacing-lg;
    border: none;
    border-radius: $radius-sm;
    background-color: $brand-color;
    color: $text-color-inverted;
    cursor: pointer;
  }
}
</style>
